import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Table, Modal } from 'react-bootstrap';
import axios from 'axios';

const NewsAlertsForm = () => {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('1');
  const [isActive, setIsActive] = useState('1'); // Added state for is_active
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newsAlerts, setNewsAlerts] = useState([]);
  const [editAlert, setEditAlert] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editAlert) {
      // Update existing news alert
      try {
        const response = await axios.put(`https://drdprints.com/api/update-news_alerts/${editAlert.id}`, {
          title,
          priority,
          is_active: isActive, // Include is_active in the update request
        });
        if (response.data) {
          setSuccess('News/Alert updated successfully!');
          setError('');
          setEditAlert(null);
          setShowEditModal(false);
          fetchNewsAlerts();
        }
      } catch (err) {
        console.error('Update error:', err.response ? err.response.data : err.message);
        setError('Failed to update news/alert. Please try again.');
        setSuccess('');
      }
    } else {
      // Add new news alert
      try {
        const response = await axios.post('https://drdprints.com/api/store-news_alerts', {
          title,
          priority,
          is_active: isActive, // Include is_active in the create request
        });
        if (response.data) {
          setSuccess('News/Alert added successfully!');
          setError('');
          setTitle('');
          setPriority('1');
          setIsActive('1'); // Reset to default
          fetchNewsAlerts(); // Fetch the updated list after adding a new alert
        }
      } catch (err) {
        console.error('Add error:', err.response ? err.response.data : err.message);
        setError('Failed to add news/alert. Please try again.');
        setSuccess('');
      }
    }
  };

  const fetchNewsAlerts = async () => {
    try {
      const response = await axios.get('https://drdprints.com/api/get-news_alerts');
      if (response.data && response.data.news_alerts) {
        setNewsAlerts(response.data.news_alerts);
      }
    } catch (err) {
      setError('Failed to fetch news/alerts. Please try again.');
    }
  };

  const handleEdit = (alert) => {
    setEditAlert(alert);
    setTitle(alert.title);
    setPriority(alert.priority);
    setIsActive(alert.is_active); // Pre-fill isActive state
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://drdprints.com/api/delete_news_alerts/${id}`);
      setSuccess('News/Alert deleted successfully!');
      setError('');
      fetchNewsAlerts();
    } catch (err) {
      console.error('Delete error:', err.response ? err.response.data : err.message);
      setError('Failed to delete news/alert. Please try again.');
      setSuccess('');
    }
  };

  useEffect(() => {
    fetchNewsAlerts();
  }, []);

  return (
    <Container fluid="md" className="my-4">
      <Row className="justify-content-center">
        <Col md={8} lg={8}>
          <h3 className="text-center mb-4">{editAlert ? 'Edit News/Alert' : 'Add News/Alert'}</h3>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter the news/alert title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formPriority" className="mt-3">
              <Form.Label>Priority</Form.Label>
              <Form.Control
                as="select"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="1">On (High Priority)</option>
                <option value="0">Off (Low Priority)</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formIsActive" className="mt-3">
              <Form.Label>Status</Form.Label>
              <Form.Control
                as="select"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Form.Control>
            </Form.Group>

            <div className="d-grid gap-2 mt-4">
              <Button variant="primary" type="submit" size="lg">
                {editAlert ? 'Update' : 'Submit'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
      <Row className="justify-content-center mt-5">
        <Col md={12}>
          <h4 className="text-center mb-4">Existing News/Alerts</h4>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {newsAlerts.map((alert) => (
                <tr key={alert.id}>
                  <td>{alert.id}</td>
                  <td>{alert.title}</td>
                  <td>{alert.priority === '1' ? 'High' : 'Low'}</td>
                  <td>{alert.is_active === '1' ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(alert.created_at).toLocaleString()}</td>
                  <td>
                    <Button variant="warning" size="sm" className='mb-2' onClick={() => handleEdit(alert)}>
                      Edit
                    </Button>{' '}
                    <Button variant="danger" size="sm" onClick={() => handleDelete(alert.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit News/Alert</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter the news/alert title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formPriority" className="mt-3">
              <Form.Label>Priority</Form.Label>
              <Form.Control
                as="select"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="1">On (High Priority)</option>
                <option value="0">Off (Low Priority)</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formIsActive" className="mt-3">
              <Form.Label>Status</Form.Label>
              <Form.Control
                as="select"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Form.Control>
            </Form.Group>

            <div className="d-grid gap-2 mt-4">
              <Button variant="primary" type="submit" size="lg">
                Update
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default NewsAlertsForm;
